/**
 * The signed-in operator's own account.
 *
 * It exists because the console could sign a user in and offer them no way to
 * change the password they signed in with. The server could all along —
 * `auth.Service.ChangePassword` is written and tested — it simply had no route
 * and no screen.
 *
 * Deliberately about *this* user and nobody else. There is no user list, no role
 * editor and no way to reach another account: those need authorisation rules the
 * service already implements properly through its own methods, and a screen that
 * reached them by omitting a parameter would be the wrong way to get there.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { Page, PageHeader, Panel, Button, KeyValue } from '@/components/primitives'
import { QueryState, TextField, Notice, useApiErrorMessage } from '@/components/controls'
import { useEditable } from '@/hooks/useEditable'
import { useT, useLocale } from '@/i18n/context'
import { formatDateTime } from '@/i18n'
import type { ID, ISOTime } from '@/types/domain'
import './ProfilePage.css'

/** api.MeResponse — the caller's own record, which carries more than the
 *  session check does: a profile screen answers different questions. */
interface MeResponse {
  userId: ID
  username: string
  displayName?: string
  email?: string
  role: string
  passwordChangedAt?: ISOTime
  mustChangePassword: boolean
  mfaEnrolled: boolean
  mfaVerified: boolean
  lastLoginAt?: ISOTime
}

export function ProfilePage() {
  const t = useT()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/me') })

  return (
    <Page header={<PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />}>
      <QueryState pending={me.isPending} error={me.error} onRetry={() => void me.refetch()}>
        {me.data && (
          <div className="k-profile">
            {me.data.mustChangePassword && (
              <Notice tone="warn">{t('profile.mustChange')}</Notice>
            )}
            <IdentityPanel me={me.data} />
            <PasswordPanel />
          </div>
        )}
      </QueryState>
    </Page>
  )
}

/** Who this account is. The username is shown and not editable: sessions, audit
 *  entries and every log line name the actor by it, so changing it would leave
 *  the record's own history pointing at somebody who no longer exists. */
function IdentityPanel({ me }: { me: MeResponse }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  // Seeded from the server, then owned by whoever types into it. `['me']` is
  // refetched on window focus and invalidated by the socket, so a plain
  // `useEffect(… setDisplayName(me.displayName) …)` would erase a half-typed
  // name at the exact moment an operator tabbed back to finish it.
  const displayName = useEditable(me.displayName ?? '')
  const email = useEditable(me.email ?? '')
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      api.patch<MeResponse>('/me', { displayName: displayName.value, email: email.value }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['me'], updated)
      // The top bar reads the actor from its own query, which now says something
      // different.
      void queryClient.invalidateQueries({ queryKey: ['auth-session'] })
      // Hand the fields back to the server: what was typed is what it holds.
      displayName.reset()
      email.reset()
      setSaved(true)
    },
    onError: () => setSaved(false),
  })

  // What Save means: the fields differ from what the server holds. Deliberately
  // not `displayName.dirty` — that says a human touched the field, which stays
  // true after they type a change back out again.
  const dirty =
    displayName.value !== (me.displayName ?? '') || email.value !== (me.email ?? '')

  return (
    <Panel title={t('profile.identity')}>
      <div className="k-form">
        <KeyValue
          items={[
            { key: 'username', label: t('profile.username'), value: me.username, mono: true },
            { key: 'role', label: t('profile.role'), value: me.role, mono: true },
            {
              key: 'mfa',
              label: t('profile.mfa'),
              value: me.mfaEnrolled ? t('profile.mfaOn') : t('profile.mfaOff'),
            },
            {
              key: 'lastLogin',
              label: t('profile.lastLogin'),
              value: me.lastLoginAt ? formatDateTime(me.lastLoginAt, locale) : t('state.never'),
            },
          ]}
        />

        <TextField
          label={t('profile.displayName')}
          hint={t('profile.displayNameHint')}
          value={displayName.value}
          onChange={(v) => {
            displayName.set(v)
            setSaved(false)
          }}
        />
        <TextField
          label={t('profile.email')}
          hint={t('profile.emailHint')}
          type="email"
          value={email.value}
          onChange={(v) => {
            email.set(v)
            setSaved(false)
          }}
        />

        {save.error && <Notice tone="err">{describe(save.error).title}</Notice>}
        {saved && !dirty && <Notice tone="ok">{t('profile.saved')}</Notice>}

        <div className="k-profile__actions">
          <Button
            variant="primary"
            size="sm"
            busy={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            {t('action.save')}
          </Button>
        </div>
      </div>
    </Panel>
  )
}

/**
 * Changing the password.
 *
 * The current password is required, which is what makes this safe without a
 * second factor: somebody holding a stolen session cookie still cannot lock the
 * owner out of their own control plane. And because the server revokes every
 * other session on success, this is also how an operator ends a session they no
 * longer trust — said in words, because it is a consequence they should expect
 * rather than discover.
 */
function PasswordPanel() {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () =>
      api.post<void>('/me/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['auth-sessions'] })
    },
    onError: () => setDone(false),
  })

  // The server owns the policy — the same object bootstrap and user creation
  // use — so this checks only what the server cannot: that the operator typed
  // the same thing twice.
  const mismatch = confirm !== '' && next !== confirm
  const canSubmit = current !== '' && next !== '' && next === confirm && !change.isPending

  return (
    <Panel title={t('profile.password')}>
      <div className="k-form">
        <p className="k-profile__lede">{t('profile.passwordLede')}</p>
        <TextField
          label={t('profile.currentPassword')}
          type="password"
          value={current}
          onChange={(v) => {
            setCurrent(v)
            setDone(false)
          }}
        />
        <TextField
          label={t('profile.newPassword')}
          hint={t('profile.newPasswordHint')}
          type="password"
          value={next}
          onChange={(v) => {
            setNext(v)
            setDone(false)
          }}
        />
        <TextField
          label={t('profile.confirmPassword')}
          type="password"
          value={confirm}
          onChange={setConfirm}
          error={mismatch ? t('setup.passwordMismatch') : undefined}
        />

        {change.error && <Notice tone="err">{describe(change.error).title}</Notice>}
        {done && <Notice tone="ok">{t('profile.passwordChanged')}</Notice>}

        <div className="k-profile__actions">
          <Button
            variant="primary"
            size="sm"
            busy={change.isPending}
            disabled={!canSubmit}
            onClick={() => change.mutate()}
          >
            {t('profile.changePassword')}
          </Button>
        </div>
      </div>
    </Panel>
  )
}
